import React, { useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { SITE_CONFIG, SEO_CONFIG } from '../config';
import { neighborhoodList } from '../data/neighborhoods';
import './SiteFooter.css';
import PrivacyChoicesLink from './PrivacyChoicesLink';

const buyerGuides = [
  ['palm-beach-county-home-buying-guide', 'Palm Beach Buyer Guide'],
  ['first-time-home-buyer-palm-beach-county', 'First-Time Buyers'],
  ['florida-homeowners-insurance-guide', 'Florida Insurance'],
  ['flood-zones-palm-beach-county', 'Flood Zones'],
  ['buying-luxury-home-south-florida', 'Luxury Homes']
];

const sellerGuides = [
  ['selling-home-palm-beach-county', 'Selling in Palm Beach'],
  ['selling-home-delray-beach', 'Selling in Delray'],
  ['luxury-home-marketing-palm-beach', 'Luxury Marketing'],
  ['selling-equestrian-property-florida', 'Equestrian Property'],
  ['selling-older-home-palm-beach-county', 'Older Homes']
];

const SiteFooter = () => {
  const { pathname } = useLocation();
  const disclosureRef = useRef(null);
  const hidden = ['/admin', '/chat', '/listing/', '/properties/list', '/add-contact', '/password-reset', '/schedule-demo'];
  if (hidden.some((prefix) => pathname === prefix || pathname.startsWith(prefix))) return null;

  if (pathname === '/agent') {
    return (
      <footer className="site-footer site-footer-about" aria-label="About Peter site links">
        <div className="site-footer-about-main">
          <Link to="/" className="site-footer-about-wordmark" aria-label="Integritos Real Estate home">
            <img src="/logo.png" alt="" />
            <span><strong>Integritos Real Estate</strong><small>South Florida Real Estate Advisors</small></span>
          </Link>
          <div className="site-footer-about-agent">
            <strong>{SITE_CONFIG.AGENT.NAME}</strong>
            <span>Broker Owner</span>
            <small>Strategy. Insight. Results.</small>
          </div>
          <div className="site-footer-about-contact">
            <a href={`tel:${SITE_CONFIG.AGENT.PHONE}`}>{SITE_CONFIG.AGENT.PHONE_DISPLAY}</a>
            <a href={`mailto:${SITE_CONFIG.AGENT.EMAIL}`}>{SITE_CONFIG.AGENT.EMAIL}</a>
            <nav aria-label="Integritos profiles">
              {SEO_CONFIG.FACEBOOK_URL && <a href={SEO_CONFIG.FACEBOOK_URL} target="_blank" rel="noopener noreferrer">Facebook</a>}
              {SEO_CONFIG.INSTAGRAM_URL && <a href={SEO_CONFIG.INSTAGRAM_URL} target="_blank" rel="noopener noreferrer">Instagram</a>}
              {SEO_CONFIG.LINKEDIN_URL && <a href={SEO_CONFIG.LINKEDIN_URL} target="_blank" rel="noopener noreferrer">LinkedIn</a>}
            </nav>
          </div>
        </div>
        <div className="site-footer-about-legal">
          <nav aria-label="Site policies">
            <Link to="/privacy-policy">Privacy</Link>
            <Link to="/terms-of-service">Terms</Link>
            <button type="button" onClick={() => disclosureRef.current?.showModal()}>MLS Disclosure</button>
          </nav>
          <div id="site-footer-language" className="site-footer-language" />
          <span>© {new Date().getFullYear()} {SITE_CONFIG.AGENT.BROKERAGE} · Florida Broker {SITE_CONFIG.AGENT.LICENSE}</span>
        </div>
        <div className="site-footer-privacy"><PrivacyChoicesLink /></div>
        <dialog className="site-footer-disclosure" ref={disclosureRef} aria-labelledby="site-footer-disclosure-title" onClick={(event) => { if (event.target === event.currentTarget) disclosureRef.current.close(); }}>
          <div>
            <h2 id="site-footer-disclosure-title">MLS Disclosure</h2>
            <p>{SITE_CONFIG.MLS.DISCLAIMER}</p>
            <p>All information is deemed reliable but not guaranteed. Listings are provided for consumers' personal, non-commercial use and may be subject to prior sale, change, or withdrawal.</p>
            <form method="dialog"><button type="submit">Close</button></form>
          </div>
        </dialog>
      </footer>
    );
  }

  return (
    <footer className="site-footer" aria-label="Site links">
      <div className="site-footer-grid">
        <div className="site-footer-brand">
          <Link to="/" className="site-footer-wordmark" aria-label="Integritos Real Estate home">
            <img src={SITE_CONFIG.LANDING_PAGE.BROKERAGE_LOGO} alt="" />
            <span><strong>Integritos Real Estate</strong><small>South Florida Real Estate Advisors</small></span>
          </Link>
          <h2>{SITE_CONFIG.AGENT.NAME}</h2>
          <p>Broker Owner</p>
          <p>Integritos Real Estate</p>
          <p className="site-footer-tagline">Strategy. Integrity. Results.</p>
        </div>
        <nav className="site-footer-areas" aria-label="Areas we serve"><h2>Areas We Serve</h2><ul>{neighborhoodList.map((area) => <li key={area.slug}><Link to={`/neighborhood/${area.slug}`}>{area.name}</Link></li>)}</ul></nav>
        <nav aria-label="Buyer guides"><h2>Buying</h2><ul>{buyerGuides.map(([slug, label]) => <li key={slug}><Link to={`/guide/${slug}`}>{label}</Link></li>)}</ul></nav>
        <nav aria-label="Seller guides"><h2>Selling</h2><ul>{sellerGuides.map(([slug, label]) => <li key={slug}><Link to={`/guide/${slug}`}>{label}</Link></li>)}</ul></nav>
        <nav aria-label="Explore"><h2>Explore</h2><ul>
          <li><Link to="/search">Property Search</Link></li><li><Link to="/guides">Buyer, Seller &amp; Lifestyle Guides</Link></li><li><Link to="/agent">Meet Peter</Link></li><li><Link to="/blogs">Journal</Link></li>
        </ul></nav>
        <div id="site-footer-contact" className="site-footer-contact" />
      </div>
      <div className="site-footer-legal">
        <div id="site-footer-language" className="site-footer-language" />
        <nav className="site-footer-utilities" aria-label="Site policies and resources">
          <Link to="/privacy-policy">Privacy</Link>
          <Link to="/terms-of-service">Service</Link>
          <button type="button" onClick={() => disclosureRef.current?.showModal()}>MLS Disclosure</button>
          <a href="https://agentwinds.com" target="_blank" rel="noopener noreferrer">Agent winds</a>
          <Link to="/api-docs">API</Link>
        </nav>
        <span>© {new Date().getFullYear()} {SITE_CONFIG.AGENT.BROKERAGE} · Florida Broker {SITE_CONFIG.AGENT.LICENSE}</span>
        <nav className="site-footer-social" aria-label="Integritos profiles">
          <Link to="/reviews">Client Reviews</Link>
          <Link to="/contact-us">Contact</Link>
          {SITE_CONFIG.AGENT.ZILLOW_PROFILE && <a href={SITE_CONFIG.AGENT.ZILLOW_PROFILE} target="_blank" rel="noopener noreferrer">Zillow Profile</a>}
          {SEO_CONFIG.FACEBOOK_URL && <a href={SEO_CONFIG.FACEBOOK_URL} target="_blank" rel="noopener noreferrer">Facebook</a>}
          {SEO_CONFIG.INSTAGRAM_URL && <a href={SEO_CONFIG.INSTAGRAM_URL} target="_blank" rel="noopener noreferrer">Instagram</a>}
        </nav>
      </div>
      <div className="site-footer-privacy"><PrivacyChoicesLink /></div>
      <dialog className="site-footer-disclosure" ref={disclosureRef} aria-labelledby="site-footer-disclosure-title" onClick={(event) => { if (event.target === event.currentTarget) disclosureRef.current.close(); }}>
        <div>
          <h2 id="site-footer-disclosure-title">MLS Disclosure</h2>
          <p>{SITE_CONFIG.MLS.DISCLAIMER}</p>
          <p>All information is deemed reliable but not guaranteed. All properties are subject to prior sale, change or withdrawal.
            Neither listing broker(s) nor information provider(s) shall be responsible for any typographical errors,
            misinformation, or misprints and shall be held totally harmless. Listing(s) information is provided for
            consumers' personal, non-commercial use and may not be used for any purpose other than to identify
            prospective properties consumers may be interested in purchasing. The data relating to real estate for
            sale on this website comes in part from the Internet Data Exchange program of {SITE_CONFIG.MLS.NAME}.
            Real estate listings held by brokerage firms other than {SITE_CONFIG.AGENT.BROKERAGE},
            may be marked with the Internet Data Exchange logo, and detailed information about those properties will
            include the name of the listing broker(s) when required by the MLS.</p>
          <form method="dialog"><button type="submit">Close</button></form>
        </div>
      </dialog>
    </footer>
  );
};

export default SiteFooter;
